import React from 'react';
import { Calendar, CalendarClock, CalendarCheck, CalendarX, FileCheck, UserX } from 'lucide-react';
import { AdminStatCard } from './AdminStatCard';
import type { OrganizationStatistics } from '@/lib/api/statistics';

interface AdminAppointmentsTabProps {
  statistics: OrganizationStatistics | null;
  isLoading: boolean;
}

export const AdminAppointmentsTab: React.FC<AdminAppointmentsTabProps> = ({
  statistics,
  isLoading,
}) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <AdminStatCard
          icon={<Calendar className="h-6 w-6" />}
          title="Total"
          value={statistics?.appointments.total || 0}
          isLoading={isLoading}
        />
        <AdminStatCard
          icon={<CalendarClock className="h-6 w-6 text-blue-600" />}
          title="Booked"
          value={statistics?.appointments.booked || 0}
          isLoading={isLoading}
        />
        <AdminStatCard
          icon={<CalendarCheck className="h-6 w-6 text-green-600" />}
          title="Arrived"
          value={statistics?.appointments.arrived || 0}
          isLoading={isLoading}
        />
        <AdminStatCard
          icon={<FileCheck className="h-6 w-6 text-emerald-600" />}
          title="Fulfilled"
          value={statistics?.appointments.fulfilled || 0}
          isLoading={isLoading}
        />
        <AdminStatCard
          icon={<CalendarX className="h-6 w-6 text-red-600" />}
          title="Cancelled"
          value={statistics?.appointments.cancelled || 0}
          isLoading={isLoading}
        />
        <AdminStatCard
          icon={<UserX className="h-6 w-6 text-orange-600" />}
          title="No-shows"
          value={statistics?.appointments.noshow || 0}
          isLoading={isLoading}
        />
      </div>
      {statistics?.appointments.todayAppointments &&
        statistics.appointments.todayAppointments.length > 0 && (
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-4 py-3">
              <h3 className="font-hanken text-sm font-semibold text-gray-700">
                Today's Appointments
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px] border-collapse">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken text-xs font-medium text-gray-600">
                      Patient
                    </th>
                    <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken text-xs font-medium text-gray-600">
                      Time
                    </th>
                    <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken text-xs font-medium text-gray-600">
                      Practitioner
                    </th>
                    <th className="whitespace-nowrap px-4 py-2.5 text-left align-middle font-hanken text-xs font-medium text-gray-600">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {statistics.appointments.todayAppointments.map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5 align-middle font-hanken text-sm text-gray-900">
                        {a.patientName}
                      </td>
                      <td className="px-4 py-2.5 align-middle font-hanken text-sm text-gray-600">
                        {a.time}
                      </td>
                      <td className="px-4 py-2.5 align-middle font-hanken text-sm text-gray-600">
                        {a.practitionerName}
                      </td>
                      <td className="px-4 py-2.5 align-middle">
                        <span
                          className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                            a.status === 'fulfilled'
                              ? 'bg-green-100 text-green-800'
                              : a.status === 'arrived'
                                ? 'bg-blue-100 text-blue-800'
                                : a.status === 'booked'
                                  ? 'bg-purple-100 text-purple-800'
                                  : a.status === 'cancelled'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {a.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
    </div>
  );
};
